// frontend/packages/common/utils/axios-widget.js
import axios from 'axios';
import {HOST_API} from 'apps/dashboard/ConfigGlobal';
import {widgetLogger, authLogger} from 'packages/common/utils/widgetLogger';

/**
 * Instance Axios dédiée aux widgets avec authentification par API key
 * Pas de gestion de tokens, pas de redirection vers login
 */
const axiosWidgetInstance = axios.create({
  baseURL: HOST_API,
  withCredentials: false, // Pas de cookies pour les widgets
});

/**
 * Configure les options pour une requête avec API key
 * @param {string} apiKey - Clé API à utiliser
 * @return {Object} - Configuration avec API key
 */
export const withApiKey = (apiKey) => {
  if (!apiKey) {
    authLogger.missingApiKey();
    return {};
  }

  // Verify we're using a valid API key format (should start with pub_)
  if (apiKey && !apiKey.startsWith('pub_')) {
    authLogger.invalidApiKey(apiKey);
    return {};
  }

  return {
    headers: {
      'x-api-key': apiKey,
    },
  };
};

/**
 * Endpoints spécifiques aux widgets (API externe uniquement)
 */
export const widgetEndpoints = {
  externalApi: {
    reservation: {
      config: (companyId) => `/v1/reservation/config/${companyId}`,
      dates: (companyId) => `/v1/reservation/dates/${companyId}`,
      timeslots: (companyId) => `/v1/reservation/timeslots/${companyId}`,
      create: (companyId) => `/v1/reservation/create/${companyId}`,
    },
    giftVoucher: {
      types: (companyId) => `/v1/gift-vouchers/catalog/${companyId}`,
      categories: (companyId) => `/v1/gift-vouchers/categories/${companyId}`,
      catalog: '/v1/gift-vouchers/catalog', // Sans companyId
      categoriesGeneral: '/v1/gift-vouchers/categories', // Sans companyId
    },
    company: {
      details: (companyId) => `/v1/company/${companyId}`,
    },
  },
};

/**
 * Intercepteur pour les requêtes des widgets
 */
axiosWidgetInstance.interceptors.request.use(
    (config) => {
      const hasApiKey = !!config.headers?.['x-api-key'];
      widgetLogger.api.request(config.method || 'GET', config.url || '', hasApiKey);
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
);

/**
 * Intercepteur pour les réponses des widgets
 * Gestion d'erreur simplifiée sans redirection
 */
axiosWidgetInstance.interceptors.response.use(
    // Succès
    (response) => {
      const method = response.config?.method || 'GET';
      const url = response.config?.url || '';
      widgetLogger.api.success(method, url, response.status);
      return response;
    },

    // Erreur
    async (error) => {
      const status = error.response?.status;
      const config = error.config;
      const method = config?.method || 'GET';
      const url = config?.url || '';

      // Log de l'erreur API
      widgetLogger.api.error(method, url, status, error);

      // Gestion spécifique des erreurs pour les widgets (logging seulement)
      switch (status) {
        case 401:
          authLogger.authenticationError();
          break;
        case 403:
          authLogger.accessDenied();
          break;
        case 429:
          const retryAfter = error.response.headers['retry-after'];
          widgetLogger.warn(`Rate limit atteint${retryAfter ? ` - Réessayer dans ${retryAfter}s` : ''}`);
          break;
        case 404:
          widgetLogger.warn('Ressource non trouvée');
          break;
        default:
          widgetLogger.error('Erreur API', error.message);
      }

      // On rejette l'erreur sans traitement supplémentaire
      // Le composant widget peut décider quoi faire
      return Promise.reject(error);
    },
);

export default axiosWidgetInstance;
